import React, { useState } from "react"

import { format } from "date-fns"
import {
  Check,
  Copy,
  Database,
  Edit,
  Eye,
  EyeOff,
  Link,
  Loader2,
  MoreHorizontal,
  Plus,
  X,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useJobPostings } from "@/hooks/useJobPostings"
import { JobPosting } from "@/types/jobPosting"

import { JobPostingDialog } from "./JobPostingDialog"

interface JobPostingsContentProps {
  jobPostings?: JobPosting[]
}

const JobPostingsContent: React.FC<JobPostingsContentProps> = ({
  jobPostings: propJobPostings,
}) => {
  const {
    jobPostings: supabaseJobPostings,
    isLoadingJobPostings,
    updateJobPosting,
  } = useJobPostings()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingJobPosting, setEditingJobPosting] = useState<JobPosting | null>(null)

  // Use job postings from props if provided, otherwise use from Supabase
  const jobPostings = supabaseJobPostings || []

  const handleEditClick = (jobPosting: JobPosting) => {
    setEditingJobPosting(jobPosting)
    setIsCreateDialogOpen(true)
  }

  const handleToggleActive = async (job: JobPosting) => {
    // If using Supabase job postings, toggle the active status
    if (supabaseJobPostings) {
      const supabaseJob = supabaseJobPostings.find((p) => p.id === job.id)
      if (supabaseJob) {
        await updateJobPosting({
          id: job.id,
          data: { active: !job.active },
        })
      }
    }
  }

  const handleToggleHidden = async (job: JobPosting) => {
    // If using Supabase job postings, toggle the active status
    if (supabaseJobPostings) {
      const supabaseJob = supabaseJobPostings.find((p) => p.id === job.id)
      if (supabaseJob) {
        await updateJobPosting({
          id: job.id,
          data: { hidden: !job.hidden },
        })
      }
    }
  }

  // Function to copy job URL with source parameter to clipboard
  const copyJobUrlToClipboard = (job: JobPosting, source: string) => {
    if (!job.url) {
      toast.error("Error", { description: "No URL available for this job posting" })
      return
    }

    // Create URL object to properly handle parameters
    const url = new URL(job.url)
    // Add source parameter
    url.searchParams.set("source", source)

    // Copy to clipboard
    navigator.clipboard
      .writeText(url.toString())
      .then(() => {
        toast.success("URL Copied", {
          description: `${
            source === "indeed" ? "Indeed" : "Hived Website"
          } URL copied to clipboard`,
        })
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err)
        toast.error("Error", { description: "Failed to copy URL to clipboard" })
      })
  }

  if (isLoadingJobPostings && !propJobPostings) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-secondary" />
        <span className="ml-2 text-secondary">Loading job postings...</span>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-secondary">Job Postings</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Job Posting
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job Title</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Vehicle Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Visibility</TableHead>
              <TableHead>Applicants</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[100px] text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobPostings.length > 0 ? (
              jobPostings.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {job.data_collection_only && (
                        <span
                          className="inline-flex items-center justify-center w-6 h-6 rounded bg-amber-100 border border-amber-200"
                          title="Data collection job - no hiring workflow"
                        >
                          <Database className="h-3.5 w-3.5 text-amber-700" />
                        </span>
                      )}
                      <span>{job.title || "N/A"}</span>
                    </div>
                  </TableCell>
                  <TableCell>{job.location || "N/A"}</TableCell>
                  <TableCell>{job.person_type || "N/A"}</TableCell>
                  <TableCell>{job.vehicle_type || "N/A"}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        job.active
                          ? "bg-green-100 text-green-800"
                          : "bg-palette-jungleGreen100 text-palette-jungleGreen800"
                      }`}
                    >
                      {job.active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        job.hidden ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {job.hidden ? "Hidden" : "Visible"}
                    </span>
                  </TableCell>
                  <TableCell>{job.num_applicants ?? "N/A"}</TableCell>
                  <TableCell>
                    {job.created_at ? format(new Date(job.created_at), "MMM d, yyyy") : "N/A"}
                  </TableCell>
                  <TableCell className="text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => handleEditClick(job)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => handleToggleActive(job)}
                        >
                          {job.active ? (
                            <X className="h-4 w-4 mr-2" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          {job.active ? "Deactivate" : "Activate"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => handleToggleHidden(job)}
                        >
                          {job.hidden ? (
                            <EyeOff className="h-4 w-4 mr-2" />
                          ) : (
                            <Eye className="h-4 w-4 mr-2" />
                          )}
                          {job.hidden ? "Show" : "Hide"}
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="cursor-pointer">
                            <Link className="h-4 w-4 mr-2" />
                            Copy Job URL for
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => copyJobUrlToClipboard(job, "indeed")}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Indeed
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => copyJobUrlToClipboard(job, "hived")}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Hived Website
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No job postings found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <JobPostingDialog
        key={editingJobPosting?.id || "new"}
        isOpen={isCreateDialogOpen}
        setIsOpen={setIsCreateDialogOpen}
        jobPosting={editingJobPosting ? editingJobPosting : null}
        onClose={() => {
          setIsCreateDialogOpen(false)
          setEditingJobPosting(null)
        }}
      />
    </div>
  )
}

export default JobPostingsContent
